import { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';

const CTASection = forwardRef<HTMLElement>((props, ref) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <section ref={ref} className="py-20 sm:py-28 relative">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[420px] bg-primary/10 rounded-full blur-[140px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="rounded-3xl border border-subtle bg-card/70 backdrop-blur-xl shadow-elevation-3 p-8 sm:p-12 lg:p-16 text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-6 rounded-full border border-subtle bg-card/60 px-3 py-1.5 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Start today</span>
          </div>

          {/* Headline */}
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight mb-3">
            Ready to start <span className="text-primary">winning</span>?
          </h2>

          {/* Subheadline */}
          <p className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto mb-8">
            Join hundreds of sales professionals who are practicing smarter.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="glass" 
              size="lg" 
              onClick={handleStart}
              className="group text-base bg-primary/20 hover:bg-primary/30 text-primary border-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20"
            >
              Start Winning
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button 
              variant="glass" 
              size="lg" 
              onClick={() => navigate('/pricing')}
              className="text-base"
            >
              View Pricing
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-8 pt-8 border-t border-border/50 flex flex-wrap justify-center gap-6 sm:gap-12 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              Professional rubric scoring
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-accent" />
              Instant feedback
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

CTASection.displayName = 'CTASection';

export default CTASection;
